import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { debounce, flatten } from "lodash";
import { SearchAndFilter } from "./SearchAndFilter";
import { ExtraItem, Resources } from "./Resources";
import { FormattedResource } from "./PublishContext";
import { useInfiniteScroll } from "./useInfiniteScroll";

import { designTokens } from "../../helpers/constants";
import { usePublishModal } from "./usePublishModal";

type ResourceState = Record<number, FormattedResource[]>;

interface BrowseResourcesProps {
  extraItems?: Array<ExtraItem>;
}

export const BrowseResources: React.FC<BrowseResourcesProps> = ({
  extraItems,
}: BrowseResourcesProps) => {
  const [allResourcesLoaded, setAllResourcesLoaded] = useState(false);

  const [resources, setResources] = useState<ResourceState>({
    0: [],
  });

  const {
    query,
    formattedData,
    pageNum,
    selectedResource,
    setPageNum,
    setPublishState,
  } = usePublishModal();

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [currentCount, setCurrentCount] = useState<number | null>(null);
  const [numFilteredResources, setNumFilteredResources] = useState(0);
  const { intersecting, scrollTarget, root } = useInfiniteScroll();

  useEffect(() => {
    return () => {
      setPublishState((prev) => ({
        ...prev,
        query: "",
        pageNum: 0,
        selectedResource: null,
      }));
    };
  }, []);

  useEffect(() => {
    setPageNum(0);
    setAllResourcesLoaded(false);
  }, [query]);

  useEffect(() => {
    if (formattedData) {
      const {
        formatted: newData,
        numFilteredOut,
        resourceCount,
      } = formattedData;

      if (pageNum === 0) {
        // set count for current query
        setCurrentCount(resourceCount);
        setNumFilteredResources(numFilteredOut);
      } else {
        setNumFilteredResources((prev) => prev + numFilteredOut);
      }

      if (pageNum === 0 && query === "") {
        // only total count when necessary
        setTotalCount((prev) =>
          prev === null || prev === 0 ? resourceCount : prev
        );
      }

      // TO DO: de-dupe!
      setResources((prev) => {
        if (pageNum === 0) {
          return { [pageNum]: newData };
        }
        return {
          ...prev,
          [pageNum]: newData,
        };
      });
    }
  }, [formattedData]);

  const allResources = React.useMemo(() => {
    const fetchedResources: FormattedResource[] = flatten(
      Object.values(resources)
    );
    const selectedResourceNotInResults =
      selectedResource === null
        ? false
        : fetchedResources.find(
            (resource) => resource.id === selectedResource?.id
          ) === undefined;

    return selectedResourceNotInResults
      ? [selectedResource, ...fetchedResources]
      : fetchedResources;
  }, [resources]);

  useEffect(() => {
    if (
      currentCount &&
      allResources.length + numFilteredResources >= currentCount
    ) {
      setAllResourcesLoaded(true);
    } else {
      setAllResourcesLoaded(false);
    }
  }, [allResources, currentCount, numFilteredResources]);

  const debounceSetPageNum = useCallback(
    debounce(
      () => setPublishState((prev) => ({ ...prev, pageNum: prev.pageNum + 1 })),
      100
    ),
    []
  );

  useEffect(() => {
    if (intersecting && !allResourcesLoaded && totalCount) {
      // TO DO: loading indicator?
      debounceSetPageNum();
    }
  }, [intersecting, allResourcesLoaded, totalCount]);

  return (
    <Wrapper ref={root} data-testid="resource-wrapper">
      <SearchAndFilter totalCount={totalCount} />

      <Resources
        scrollTarget={scrollTarget}
        resources={allResources}
        totalCount={totalCount}
        currentCount={currentCount}
        extraItems={extraItems}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div({
  background: designTokens["ds-color-gray-lightest"],
  overflowY: "auto",
  scrollBehavior: "smooth",
});
